import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/react";
import { toast } from "sonner";
import { Gift, Loader2, Copy, Check, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Btn, BtnLink, Card, Eyebrow } from "@/components/kit";
import { FullLoader } from "@/components/auth/AccessGate";
import { Reassurance, CardLogos, TermsConsent } from "@/components/PaymentTrust";
import { useApi } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const STEPS = [
  "Tu règles 9 € une seule fois.",
  "Tu reçois un code cadeau, ici et par email.",
  "Tu le transmets à ton amie, comme tu veux.",
  "Elle crée son compte et saisit le code : son parcours est débloqué.",
];

export function GiftCodeRow({ g }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (await copyText(g.code)) {
      setCopied(true);
      toast.success("Code copié");
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white px-4 py-3" data-testid={`gift-row-${g.code}`}>
      <div>
        <p className="font-display text-lg tracking-[0.12em] text-bx" data-testid="gift-code">{g.code}</p>
        <p className="text-xs text-soft">{g.redeemed ? "Déjà utilisé par ton amie" : "Pas encore utilisé"}</p>
      </div>
      <Btn variant="outline" size="sm" onClick={copy} disabled={g.redeemed} data-testid="gift-copy-button">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}Copier
      </Btn>
    </li>
  );
}

export default function GiftPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApi();
  const [gifts, setGifts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);

  const load = useCallback(() => api("GET", "/gifts").then((r) => setGifts(r.gifts)).catch(() => setGifts([])), [api]);
  useEffect(() => { if (isLoaded && isSignedIn) load(); }, [isLoaded, isSignedIn, load]);

  if (!isLoaded) return <FullLoader />;

  const buy = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await api("POST", "/payments/checkout", { origin_url: window.location.origin, gift: true, accepted_terms: terms });
      window.location.href = checkout_url;
    } catch {
      setLoading(false);
      toast.error("Le paiement n'a pas pu démarrer. Réessaie dans un instant.");
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="gift-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
        <Logo />
        {isSignedIn ? <UserButton /> : <BtnLink to="/sign-in" variant="outline" size="sm" data-testid="gift-sign-in-link">Se connecter</BtnLink>}
      </header>
      <main className="mx-auto grid max-w-5xl gap-8 px-5 pb-20 pt-6 sm:px-8 lg:grid-cols-[1.2fr_1fr] lg:pt-12">
        <section className="animate-fade-up">
          <Eyebrow>Offrir UGC GO</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold leading-tight text-bx sm:text-5xl">Offre sa première vidéo UGC à une amie.</h1>
          <p className="mt-4 max-w-lg text-lg text-soft">Le parcours complet « Ma première vidéo UGC », à offrir à une débutante qui veut se lancer.</p>
          <ol className="mt-8 space-y-3">
            {STEPS.map((t, i) => (
              <li key={t} className="flex gap-4 text-ink">
                <span className="font-display text-2xl leading-none text-gold lining-nums">{i + 1}</span>
                <span className="pt-0.5">{t}</span>
              </li>
            ))}
          </ol>
          {isSignedIn && gifts?.length > 0 && (
            <div className="mt-10" data-testid="my-gifts">
              <h2 className="font-display text-xl font-semibold text-bx">Mes codes cadeaux</h2>
              <ul className="mt-4 space-y-3">{gifts.map((g) => <GiftCodeRow key={g.code} g={g} />)}</ul>
            </div>
          )}
        </section>
        <Card className="h-fit lg:sticky lg:top-8" data-testid="gift-offer-card">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ivory text-bx"><Gift className="h-5 w-5" strokeWidth={1.6} /></span>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-soft">Parcours en cadeau</p>
          <p className="mt-3 flex items-baseline gap-2"><span className="font-display text-6xl text-bx" data-testid="gift-price">9 €</span><span className="text-soft">TTC</span></p>
          <p className="mt-1 text-sm font-medium text-bx-2">Paiement unique, code utilisable une fois</p>
          <div className="mt-6 space-y-3">
            {isSignedIn ? (
              <>
                <TermsConsent checked={terms} onChange={setTerms} testId="gift-terms" />
                <Btn size="lg" className="w-full" onClick={buy} disabled={loading || !terms} data-testid="gift-buy-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}Offrir le parcours · 9 €
                </Btn>
              </>
            ) : (
              <>
                <p className="text-sm text-soft">Connecte-toi ou crée ton compte pour offrir le parcours : ton code cadeau y sera conservé.</p>
                <BtnLink to="/sign-up" size="lg" className="w-full" data-testid="gift-sign-up-button">Créer mon compte</BtnLink>
                <BtnLink to="/sign-in" variant="outline" size="lg" className="w-full" data-testid="gift-have-account-button">J'ai déjà un compte</BtnLink>
              </>
            )}
          </div>
          <Reassurance testId="gift-reassurance" className="mt-4" />
          <CardLogos testId="gift-card-logos" className="mt-3" />
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-bx underline-offset-4 hover:underline" data-testid="gift-back-home"><ArrowLeft className="h-4 w-4" />Retour à l'accueil</Link>
        </Card>
      </main>
    </div>
  );
}
